/**
 * The module's outcomes, to hand while writing something that maps to them.
 *
 * @module components/studio/MimloReference
 */

import React from "react";

import { Accordion, AccordionItem, Badge, countLabel, Text } from "@/components/ui";
import type { MIMLO } from "@/types";

export interface MimloReferenceProps {
  mimlos: readonly MIMLO[];
  /** What the reader is doing with them, for the toggle's own name. */
  scope: string;
  "data-testid": string;
}

export function MimloReference({
  mimlos,
  scope,
  "data-testid": testId,
}: MimloReferenceProps): React.ReactElement | null {
  if (!mimlos.length) {
    return null;
  }

  return (
    <div className="mt-5" data-testid={testId}>
      <Accordion id={`${testId}-accordion`}>
        <AccordionItem
          eventKey={testId}
          headingLevel={4}
          title={`Module outcomes for ${scope}`}
          headerActions={
            <Badge tone="neutral" aria-label={countLabel(mimlos.length, "outcome")}>
              {mimlos.length}
            </Badge>
          }
        >
          <ol className="divide-y divide-base-300">
            {mimlos.map((mimlo, index) => (
              <li key={mimlo.id} className="grid gap-2 py-3 sm:grid-cols-[6rem_1fr]">
                <span className="font-semibold">Outcome {index + 1}</span>
                <Text as="span" tone="muted">
                  {mimlo.text || "No outcome wording has been added yet."}
                </Text>
              </li>
            ))}
          </ol>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export default MimloReference;
